import { useState, useEffect } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Input } from './Input';
import { AssetManagement } from './AssetManagement';
import { PriceAlertComponent } from './PriceAlert';
import { ApiKeyCard } from './ApiKeyCard';
import { AktoolsConfigCard } from './AktoolsConfigCard';
import { ExchangeRateConfigCard } from './ExchangeRateConfigCard';
import { useThemeStore } from '@/stores/themeStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useTranslation } from 'react-i18next';
import { twelvedataService } from '@/services/twelvedata';
import { safeInvoke, isTauri } from '@/utils/tauri';
import { providerRegistry } from '@/services/providerRegistry';

export const Settings = () => {
  const { theme, setTheme } = useThemeStore();
  const {
    updateInterval,
    autoStart,
    language,
    twelvedataApiKey,
    setUpdateInterval,
    setAutoStart,
    setLanguage,
    setTwelvedataApiKey,
  } = useSettingsStore();
  const { t, i18n } = useTranslation();

  const [apiKeyInput, setApiKeyInput] = useState(twelvedataApiKey || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    valid: boolean;
    message: string;
  } | null>(null);

  // 当存储中的API key变化时，更新输入框
  useEffect(() => {
    setApiKeyInput(twelvedataApiKey || '');
  }, [twelvedataApiKey]);

  // 检查并同步开机启动状态
  useEffect(() => {
    if (isTauri()) {
      safeInvoke<boolean>('is_autostart_enabled').then((enabled) => {
        if (enabled !== null && enabled !== autoStart) {
          setAutoStart(enabled);
        }
      });
    }
  }, []); // 只在组件挂载时执行一次

  const handleLanguageChange = (lang: 'zh' | 'en') => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const handleTestApiKey = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await twelvedataService.testApiKey(apiKeyInput || undefined);
      setTestResult({
        valid: result.valid,
        message: result.message,
      });

      // 如果测试成功，保存API密钥
      if (result.valid && apiKeyInput) {
        setTwelvedataApiKey(apiKeyInput);
        twelvedataService.setApiKey(apiKeyInput);
      }
    } catch (error: any) {
      setTestResult({
        valid: false,
        message: error.message || '测试失败',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveApiKey = () => {
    if (apiKeyInput) {
      setTwelvedataApiKey(apiKeyInput);
      twelvedataService.setApiKey(apiKeyInput);
      setTestResult({
        valid: true,
        message: 'API密钥已保存',
      });
    } else {
      setTwelvedataApiKey(null);
      twelvedataService.setApiKey(null);
      setTestResult({
        valid: true,
        message: '已清除API密钥，将使用demo模式',
      });
    }
  };

  const handleClearApiKey = () => {
    setApiKeyInput('');
    setTwelvedataApiKey(null);
    twelvedataService.setApiKey(null);
    setTestResult(null);
  };

  return (
    <div className="w-full space-y-4">

      <AssetManagement />

      <Card>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.theme')}
            </label>
            <div className="flex gap-2">
              <Button
                variant={theme === 'light' ? 'primary' : 'secondary'}
                onClick={() => setTheme('light')}
              >
                {t('settings.light')}
              </Button>
              <Button
                variant={theme === 'dark' ? 'primary' : 'secondary'}
                onClick={() => setTheme('dark')}
              >
                {t('settings.dark')}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.language')}
            </label>
            <div className="flex gap-2">
              <Button
                variant={language === 'zh' ? 'primary' : 'secondary'}
                onClick={() => handleLanguageChange('zh')}
              >
                {t('settings.chinese')}
              </Button>
              <Button
                variant={language === 'en' ? 'primary' : 'secondary'}
                onClick={() => handleLanguageChange('en')}
              >
                {t('settings.english')}
              </Button>
            </div>
          </div>

          <Input
            label={t('settings.updateInterval')}
            type="number"
            value={updateInterval}
            onChange={(e) => setUpdateInterval(Number(e.target.value))}
            min={5}
            max={300}
          />

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.autoStart')}
            </label>
            <input
              type="checkbox"
              checked={autoStart}
              onChange={async (e) => {
                const enabled = e.target.checked;
                setAutoStart(enabled);

                // 调用 Tauri 命令设置开机启动
                if (isTauri()) {
                  try {
                    if (enabled) {
                      await safeInvoke('enable_autostart');
                    } else {
                      await safeInvoke('disable_autostart');
                    }
                  } catch (error) {
                    console.error('设置开机启动失败:', error);
                    // 如果失败，恢复原来的状态
                    setAutoStart(!enabled);
                  }
                }
              }}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Price Alert Component - Hidden */}
      {/* <PriceAlertComponent /> */}

      {/* API Keys Configuration - Grouped by Asset Type */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('settings.apiKeys.title')}
        </h2>

        {/* 加密货币数据源 */}
        <ApiKeyCard
          assetType="crypto"
          providers={[
            { provider: 'coingecko', name: 'CoinGecko', priority: 1, requiresKey: false },
            { provider: 'cryptowatchr', name: 'CryptowatchR', priority: 2, requiresKey: false },
            { provider: 'twelvedata', name: 'TwelveData', priority: 3, requiresKey: true },
            { provider: 'coinmarketcap', name: 'CoinMarketCap', priority: 4, requiresKey: true },
          ]}
        />

        {/* 股票数据源 */}
        <ApiKeyCard
          assetType="stock"
          providers={[
            { provider: 'finnhub', name: 'Finnhub', priority: 1, requiresKey: true },
            { provider: 'alphavantage', name: 'Alpha Vantage', priority: 2, requiresKey: true },
            { provider: 'twelvedata', name: 'TwelveData', priority: 3, requiresKey: true },
            { provider: 'marketstack', name: 'Marketstack', priority: 4, requiresKey: true },
          ]}
        />

        {/* 指数数据源 - 仅 Financial Modeling Prep 支持 */}
        <ApiKeyCard
          assetType="index"
          providers={[
            { provider: 'financialmodelingprep', name: 'Financial Modeling Prep', priority: 1, requiresKey: true },
          ]}
        />

        {/* 汇率数据源 */}
        <ApiKeyCard
          assetType="forex"
          providers={[
            { provider: 'alphavantage', name: 'Alpha Vantage', priority: 1, requiresKey: true },
            { provider: 'finnhub', name: 'Finnhub', priority: 2, requiresKey: true },
            { provider: 'twelvedata', name: 'TwelveData', priority: 3, requiresKey: true },
          ]}
        />

        {/* Aktools 数据源 */}
        <AktoolsConfigCard />

        {/* 汇率配置 */}
        <ExchangeRateConfigCard />
      </div>

      <Card>
        <div className="flex flex-col items-center text-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.about.title')}
          </h3>
          <div className="space-y-1">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              version 1.0.0
            </div>
            <div className="text-sm text-gray-800 dark:text-gray-200">
              {t('settings.about.name')}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              support@btc-reborn.com
            </div>
            <a
              href="https://price.btc-reborn.com"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
            >
              price.btc-reborn.com
            </a>
          </div>
        </div>
      </Card>

    </div>
  );
};

